import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  InputAdornment,
  Chip,
  Stack, 
  Container,
  Avatar,
  Divider,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import * as HeroIcons from '@heroicons/react/outline';
import { PlusIcon, DocumentAddIcon } from '@heroicons/react/outline';
import { portant } from '../../theme/portantTheme';
import { createWorkflowUrl } from '../Configs/Configs';
import Header from './Header';
import { GetWorkFlows } from '../Services/GetWorkFlows';
import WorkflowDetailTaskpane from './WorkflowDetailTaskpane';
import WorkflowCardSkeleton from '../Loader/Scalaton';
import AppLoader from '../Loader/Loader';
import { useNavigate } from 'react-router-dom';

interface WorkflowResult {
  id?: string;
  name: string;
  description?: string;
  status?: string;
  updatedAt?: string;
  owner?: { fullName: string };
  previewDocumentType?: string;
  icon?: string;
  color?: string;
  sourceFields: any[];
}

// The API stores each workflow's icon as a Heroicons name (e.g. "DocumentText",
// "DocumentAdd"), the same set the web app picks from. Resolve it to the
// matching @heroicons/react outline component (components are suffixed "Icon"),
// falling back to a generic document icon for anything unknown.
const resolveWorkflowIcon = (name?: string): React.ComponentType<any> => {
  const icons = HeroIcons as Record<string, any>;
  return icons[`${name}Icon`] || icons.DocumentTextIcon;
};

// Pick a legible foreground (white or ink-black) for a given workflow colour,
// so a light user-chosen colour (or the "#cccccc" default) stays readable.
const readableOn = (hex?: string): string => {
  const value = (hex || '').replace('#', '');
  if (value.length !== 6) return portant.white;
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? portant.inkBlack : portant.white;
};

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [isrefreshed, setisrefreshed] = useState(false);

  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowResult | null>(null);
  const [workflows, setWorkflows] = useState<WorkflowResult[]>([]);
  
  // States for handling different loading scenarios
  const [loading, setLoading] = useState(true); // For skeletons
  const [loadingSpin, setLoadingspin] = useState(false); // For full page overlay (e.g. Logout)
  const [isCheckingAuth, setIsCheckingAuth] = useState(true); // For initial entry

  const TOKEN = localStorage.getItem("addin_token") || "";

  useEffect(() => {
    // 1. If no token exists at all, immediately kick to login
    if (!TOKEN) {
      navigate("/");
      return;
    }

    setLoading(true);
    
    // 2. Fetch workflows - this acts as our "Session Check"
    GetWorkFlows(TOKEN, (data, error) => {
      setIsCheckingAuth(false); // We've received a response, stop initial auth check

      if (data) {
        try {
          const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
          const list = Array.isArray(parsedData) ? parsedData : (parsedData.results || []);
          setWorkflows(list);
        } catch (e) {
          console.error("Parsing error", e);
          setWorkflows([]);
        }
        setLoading(false);
      }

      if (error) {
        console.error("Auth expired or API error:", error);
        // 3. Clear storage and redirect on error (usually 401 Unauthorized)
        localStorage.removeItem("addin_token");
        navigate("/");
      }
    });
  }, [TOKEN, navigate,isrefreshed]);

  const filteredWorkflows = workflows.filter((w) => 
    w.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // While checking if the token is valid upon page load
  if (isCheckingAuth) {
    return <AppLoader />;
  }

  return (
    <>
      {selectedWorkflow ? (
        <WorkflowDetailTaskpane
            workflowName={selectedWorkflow.name}
            selectedWorkfloFields={selectedWorkflow.sourceFields}
            onBack={() => setSelectedWorkflow(null)}
        />
      ) : (
        <Box sx={{ bgcolor: portant.canvas, minHeight: '95vh' }}>
          <Header  setisrefreshed={setisrefreshed}/>

          <Container maxWidth="lg" sx={{ pt: 2, pb: 6 }}>
            <Typography variant="overline" sx={{ color: portant.orange, display: 'block', mb: 0.5 }}>
              Your workflows
            </Typography>
            <Typography variant="h5" sx={{ mb: 2.5 }}>
              Personalise with HubSpot properties
            </Typography>

            <TextField
              fullWidth
              placeholder="Search workflows..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: portant.charred }} />
                  </InputAdornment>
                ),
              }}
            />

            {loading ? (
              <Stack spacing={2}>
                {[...Array(3)].map((_, i) => <WorkflowCardSkeleton key={i} />)}
              </Stack>
            ) : (
              <Stack spacing={2}>
                {filteredWorkflows.length > 0 ? (
                  filteredWorkflows.map((wkf, index) => {
                    const WorkflowIcon = resolveWorkflowIcon(wkf.icon);
                    const iconColor = wkf.color || portant.inkBlack;
                    return (
                    <Card
                      key={wkf.id || index}
                      sx={{
                        '&:hover': {
                          borderColor: portant.darkParchment,
                          boxShadow: '0 4px 6px -1px rgba(15,23,42,0.08), 0 12px 24px -4px rgba(15,23,42,0.10)',
                        },
                      }}
                    >
                      <CardActionArea onClick={() => setSelectedWorkflow(wkf)}>
                        <CardContent sx={{ p: 2.25 }}>
                          <Stack direction="row" spacing={1.75} alignItems="center">
                            <Avatar
                              variant="rounded"
                              sx={{ bgcolor: iconColor, borderRadius: 2, width: 40, height: 40 }}
                            >
                              <WorkflowIcon style={{ width: 21, height: 21, color: readableOn(iconColor) }} />
                            </Avatar>
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography variant="subtitle1" noWrap>{wkf.name}</Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {wkf.sourceFields?.length || 0} {wkf.sourceFields?.length === 1 ? 'property' : 'properties'}
                              </Typography>
                            </Box>
                            <ChevronRightIcon sx={{ color: portant.charred }} />
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                    );
                  })
                ) : workflows.length === 0 ? (
                  // Signed in, but the account has no workflows yet — point the
                  // user to the Portant web app, where `?new=true` opens the
                  // create-workflow flow on load.
                  <Box sx={{ textAlign: 'center', py: 5, px: 2 }}>
                    <Avatar
                      variant="rounded"
                      sx={{ bgcolor: portant.lightOrange, width: 56, height: 56, borderRadius: 3, mx: 'auto', mb: 2 }}
                    >
                      <DocumentAddIcon style={{ width: 28, height: 28, color: portant.orange }} />
                    </Avatar>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      No workflows yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 300, mx: 'auto' }}>
                      Create your first workflow in Portant to start inserting merge tags and signature
                      fields into your documents.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      href={createWorkflowUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<PlusIcon style={{ width: 18, height: 18 }} />}
                    >
                      Create a workflow
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography color="text.secondary">
                      No workflows match &ldquo;{searchTerm}&rdquo;.
                    </Typography>
                  </Box>
                )}
              </Stack>
            )}

            {/* Global Loader for Header actions like logout */}
            {loadingSpin && <AppLoader />}
          </Container>
        </Box>
      )}
    </>
  );
};

export default HomePage;